import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { ArrowLeft, Shield, Users, Info } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { getMyteamPlayer, getMyTeamGoalies } from '../../lib/supabase';
import { 
  addToProtectionList, 
  removeFromProtectionList, 
  getProtectionList, 
  getProtectionLimits,
  validateProtectionLimit 
} from '../../lib/api/protectionList';
import { ProtectionListTable } from './ProtectionListTable';
import { ProtectionListGoalieTable } from './ProtectionListGoalieTable';
import { ProtectedPlayersTable } from './ProtectedPlayersTable';
import { ProtectedGoaliesTable } from './ProtectedGoaliesTable';
import { OtherDGsProtectionTab } from './OtherDGsProtectionTab';
import toast from 'react-hot-toast';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';

type ActiveTab = 'all' | 'protected' | 'other-dgs';
type PlayerType = 'players' | 'goalies';

interface Player {
  playerid: number;
  skaterfullname: string;
  positioncode: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  goals: number;
  assists: number;
  points: number;
  isnhl: boolean;
}

interface Goalie {
  playerid: number;
  goaliefullname: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  wins: number;
  losses: number;
  shutouts: number;
  savepct: number;
  goals: number;
  assists: number;
  isnhl: boolean;
}

interface ProtectionLimits {
  forwards: number;
  defensemen: number;
  goalies: number;
  reserves: number; // Added reserves property
}

export function ProtectionList() {
  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();
  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [playerType, setPlayerType] = useState<PlayerType>('players');
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [allGoalies, setAllGoalies] = useState<Goalie[]>([]);
  const [protectedPlayerIds, setProtectedPlayerIds] = useState<number[]>([]);
  const [protectionLimits, setProtectionLimits] = useState<ProtectionLimits>({
    forwards: 8,
    defensemen: 3,
    goalies: 1,
    reserves: 0 // Initialize reserves
  });
  const [loading, setLoading] = useState(true);
  const [deadline, setDeadline] = useState<string | null>(null);
  const [isProtectionListEnabled, setIsProtectionListEnabled] = useState(false);

  useEffect(() => {
    if (!selectedLeague || !currentUserId) {
      navigate('/dashboard');
      return;
    }

    async function loadData() {
      try {
        setLoading(true);

        // Fetch players
        const { data: playersData, error: playersError } = await getMyteamPlayer(
          selectedLeague!.id!,
          Number(currentUserId)
        );

        if (playersError) throw playersError;

        // Fetch goalies
        const { data: goaliesData, error: goaliesError } = await getMyTeamGoalies(
          selectedLeague!.id!,
          Number(currentUserId)
        );

        if (goaliesError) throw goaliesError;

        // Fetch protection list
        const { data: protectionData, error: protectionError } = await getProtectionList(
          Number(currentUserId),
          selectedLeague!.id!
        );

        if (protectionError) throw protectionError;

        // Fetch protection limits
        const { data: limitsData, error: limitsError } = await getProtectionLimits(
          selectedLeague!.id!
        );

        if (limitsError) throw limitsError;

        setAllPlayers(playersData || []);
        setAllGoalies(goaliesData || []);
        setProtectedPlayerIds(protectionData);
        setProtectionLimits({
          ...limitsData,
          reserves: limitsData.reserves || 0 // Ensure reserves is set
        });
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [selectedLeague, currentUserId, navigate]);

  useEffect(() => {
    if (!selectedLeague) return;

    async function fetchDeadline() {
      try {
        if (!selectedLeague) return; // Additional null check

        const { data, error } = await getLeagueSettings(selectedLeague.id);
        if (error) throw error;

        if (data?.maxdate_protectionlist) {
          const formattedDate = new Date(data.maxdate_protectionlist).toLocaleDateString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          setDeadline(formattedDate);
        }
      } catch (error) {
        console.error('Error fetching deadline:', error);
      }
    }

    fetchDeadline();
  }, [selectedLeague, language]);

  useEffect(() => {
    if (!selectedLeague) return;

    async function checkProtectionListMode() {
      try {
        if (!selectedLeague) return; // Additional null check

        const { data, error } = await getLeagueSettings(selectedLeague.id);
        if (error) throw error;

        setIsProtectionListEnabled(data?.protectionlist ?? false);
      } catch (error) {
        console.error('Error checking protection list mode:', error);
      }
    }

    checkProtectionListMode();
  }, [selectedLeague]);

  const handleAddToProtection = async (player: Player | Goalie) => {
    if (!selectedLeague || !currentUserId) return;

    const isGoalie = 'goaliefullname' in player;
    const isReserve = !player.isnhl; // Determine if the player is a reserve
    const playerPosition = isGoalie ? 'G' : (player as Player).positioncode;

    try {
      // Validate protection limit before adding
      const validation = await validateProtectionLimit(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id,
        playerPosition,
        isGoalie,
        isReserve // Pass isReserve to skip limit checks for reserve players
      );

      if (!validation.canAdd) {
        toast.error(validation.error || 'Limite de protection atteinte');
        return;
      }

      const { error } = await addToProtectionList(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast.error('Ce joueur est déjà dans votre liste de protection');
        } else {
          throw error;
        }
        return;
      }

      setProtectedPlayerIds([...protectedPlayerIds, player.playerid]);

      const playerName = 'skaterfullname' in player 
        ? player.skaterfullname 
        : player.goaliefullname;

      toast.success(`${playerName} ajouté à la liste de protection`);
    } catch (error) {
      console.error('Error adding to protection list:', error);
      toast.error('Erreur lors de l\'ajout à la liste de protection');
    }
  };

  const handleRemoveFromProtection = async (player: Player | Goalie) => {
    if (!selectedLeague || !currentUserId) return;

    try {
      const { error } = await removeFromProtectionList(
        player.playerid,
        Number(currentUserId),
        selectedLeague.id
      );

      if (error) throw error;

      setProtectedPlayerIds(protectedPlayerIds.filter(id => id !== player.playerid));
      
      const playerName = 'skaterfullname' in player 
        ? player.skaterfullname 
        : player.goaliefullname;
      
      toast.success(`${playerName} retiré de la liste de protection`);
    } catch (error) {
      console.error('Error removing from protection list:', error);
      toast.error('Erreur lors du retrait de la liste de protection');
    }
  };

  const getProtectedPlayers = () => {
    return allPlayers.filter(player => protectedPlayerIds.includes(player.playerid));
  };

  const getProtectedGoalies = () => {
    return allGoalies.filter(goalie => protectedPlayerIds.includes(goalie.playerid));
  };

  const getReservePlayers = () => {
    return [...allPlayers, ...allGoalies].filter(player => !player.isnhl && protectedPlayerIds.includes(player.playerid));
  };

  const getUnprotectedPlayers = () => {
    return allPlayers.filter(player => !protectedPlayerIds.includes(player.playerid));
  };

  const getUnprotectedGoalies = () => {
    return allGoalies.filter(goalie => !protectedPlayerIds.includes(goalie.playerid));
  };

  const getProtectionCounts = () => {
    const protectedPlayers = getProtectedPlayers();
    const protectedGoalies = getProtectedGoalies();
    const reservePlayers = getReservePlayers();

    const forwards = protectedPlayers.filter(p => ['C', 'L', 'R'].includes(p.positioncode) && p.isnhl).length;
    const defensemen = protectedPlayers.filter(p => p.positioncode === 'D' && p.isnhl).length;
    const goalies = protectedGoalies.filter(g => g.isnhl).length;
    const reserves = reservePlayers.length;

    return { forwards, defensemen, goalies, reserves };
  };

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="card shadow-lg border-0">
        <div className="card-body p-5 text-center">
          <div className="spinner-border text-primary\" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  // Mode guard
  if (!modeLoading && isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="dynasty-only"
        title="Liste de protection — Mode Dynasty / Keeper uniquement"
        message="La liste de protection est une fonctionnalité exclusive au mode Dynasty/Keeper. En mode Standard Flexible, il n'y a pas de liste de protection ni de système keeper."
      />
    );
  }

  if (!isProtectionListEnabled) {
    toast.error('Le mode liste de protection doit être activé pour accéder à cette page.');
    navigate('/my-office');
    return null;
  }

  const totalProtected = getProtectedPlayers().length + getProtectedGoalies().length;
  const protectionCounts = getProtectionCounts();

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Shield className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-2">Liste de Protection</h2>
          <p className="text-muted">{selectedLeague?.name}</p>
        </div>

        <div className="alert alert-info mb-4">
          <div className="d-flex align-items-center">
            <Shield size={18} className="text-primary me-2" />
            <div>
              <strong>Information:</strong> Sélectionnez les joueurs que vous souhaitez protéger pour la fin de saison. 
              Les joueurs non protégés seront disponibles pour le repêchage d'expansion.
            </div>
          </div>
        </div>

        {/* Protection Limits Info */}
        <div className="card mb-4 border-0 bg-light">
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <Info size={20} className="text-primary me-2" />
              <h5 className="mb-0">Limites de Protection</h5>
            </div>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">Attaquants</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Protégés:</span>
                      <span className={`fw-bold ${protectionCounts.forwards > protectionLimits.forwards ? 'text-danger' : ''}`}>
                        {protectionCounts.forwards}/{protectionLimits.forwards}
                      </span>
                    </div>
                    <div className="progress mt-2" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar ${
                          protectionCounts.forwards > protectionLimits.forwards ? 'bg-danger' : 'bg-success'
                        }`}
                        role="progressbar"
                        style={{ width: `${Math.min((protectionCounts.forwards / protectionLimits.forwards) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">Défenseurs</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Protégés:</span>
                      <span className={`fw-bold ${protectionCounts.defensemen > protectionLimits.defensemen ? 'text-danger' : ''}`}>
                        {protectionCounts.defensemen}/{protectionLimits.defensemen}
                      </span>
                    </div>
                    <div className="progress mt-2" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar ${
                          protectionCounts.defensemen > protectionLimits.defensemen ? 'bg-danger' : 'bg-success'
                        }`}
                        role="progressbar"
                        style={{ width: `${Math.min((protectionCounts.defensemen / protectionLimits.defensemen) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">Gardiens</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Protégés:</span>
                      <span className={`fw-bold ${protectionCounts.goalies > protectionLimits.goalies ? 'text-danger' : ''}`}>
                        {protectionCounts.goalies}/{protectionLimits.goalies}
                      </span>
                    </div>
                    <div className="progress mt-2" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar ${
                          protectionCounts.goalies > protectionLimits.goalies ? 'bg-danger' : 'bg-success'
                        }`}
                        role="progressbar"
                        style={{ width: `${Math.min((protectionCounts.goalies / protectionLimits.goalies) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">Réserves</h6>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Protégés:</span>
                      <span className={`fw-bold ${protectionCounts.reserves > protectionLimits.reserves ? 'text-danger' : ''}`}>
                        {protectionCounts.reserves}/{protectionLimits.reserves}
                      </span>
                    </div>
                    <div className="progress mt-2" style={{ height: '8px' }}>
                      <div
                        className={`progress-bar ${
                          protectionCounts.reserves > protectionLimits.reserves ? 'bg-danger' : 'bg-success'
                        }`}
                        role="progressbar"
                        style={{ width: `${Math.min((protectionCounts.reserves / protectionLimits.reserves) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {deadline && (
          <div className="alert alert-warning mb-4">
            <strong>Date limite:</strong> {deadline}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="btn-group w-100 mb-4">
          <button
            className={`btn ${activeTab === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('all')}
          >
            <Users size={18} className="me-2" />
            Tous les Joueurs
          </button>
          <button
            className={`btn ${activeTab === 'protected' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('protected')}
          >
            <Shield size={18} className="me-2" />
            Joueurs Protégés ({totalProtected})
          </button>
          <button
            className={`btn ${activeTab === 'other-dgs' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('other-dgs')}
          >
            <Users size={18} className="me-2" />
            Autres DGs
          </button>
        </div>

        {/* Player Type Navigation — masqué pour l'onglet Autres DGs */}
        {activeTab !== 'other-dgs' && (
          <ul className="nav nav-pills mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${playerType === 'players' ? 'active' : ''}`}
                onClick={() => setPlayerType('players')}
              >
                <Users size={18} className="me-2" />
                Joueurs
              </button>
            </li>
            <li className="nav-item ms-2">
              <button
                className={`nav-link ${playerType === 'goalies' ? 'active' : ''}`}
                onClick={() => setPlayerType('goalies')}
              >
                <Users size={18} className="me-2" />
                Gardiens
              </button>
            </li>
          </ul>
        )}

        {/* Content based on active tab */}
        {activeTab === 'all' && (
          <>
            {playerType === 'players' && (
              <>
                {getUnprotectedPlayers().length === 0 ? (
                  <div className="text-center p-5">
                    <p>Tous vos joueurs sont déjà protégés.</p>
                  </div>
                ) : (
                  <ProtectionListTable
                    players={getUnprotectedPlayers()}
                    onAddToProtection={handleAddToProtection}
                    language={language}
                  />
                )}
              </>
            )}

            {playerType === 'goalies' && (
              <>
                {getUnprotectedGoalies().length === 0 ? (
                  <div className="text-center p-5">
                    <p>Tous vos gardiens sont déjà protégés.</p>
                  </div>
                ) : (
                  <ProtectionListGoalieTable
                    goalies={getUnprotectedGoalies()}
                    onAddToProtection={handleAddToProtection}
                    language={language}
                  />
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'protected' && (
          <>
            {playerType === 'players' && (
              <>
                {getProtectedPlayers().length === 0 ? (
                  <div className="text-center p-5">
                    <p>Aucun joueur protégé.</p>
                  </div>
                ) : (
                  <ProtectedPlayersTable
                    players={getProtectedPlayers()}
                    onRemoveFromProtection={handleRemoveFromProtection}
                    language={language}
                  />
                )}
              </>
            )}

            {playerType === 'goalies' && (
              <>
                {getProtectedGoalies().length === 0 ? (
                  <div className="text-center p-5">
                    <p>Aucun gardien protégé.</p>
                  </div>
                ) : (
                  <ProtectedGoaliesTable
                    goalies={getProtectedGoalies()}
                    onRemoveFromProtection={handleRemoveFromProtection}
                    language={language}
                  />
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'other-dgs' && (
          <OtherDGsProtectionTab
            leagueId={selectedLeague.id!}
            currentUserId={Number(currentUserId)}
            language={language}
          />
        )}
      </div>
    </div>
  );
}